import Service, { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

export default class CurrentUserService extends Service {
  @service session;
  @service store;
  @service router;

  @tracked certificationPointOfContact;
  @tracked currentAllowedCertificationCenterAccess;
  @tracked currentCertificationCenterMembership;
  @tracked isAdminOfCurrentCertificationCenter;

  async load() {
    if (this.session.isAuthenticated) {
      try {
        this.certificationPointOfContact = await this.store.queryRecord('certification-point-of-contact', {});

        const allowedCertificationCenterAccesses = this.certificationPointOfContact
          .hasMany('allowedCertificationCenterAccesses')
          .value();

        if (allowedCertificationCenterAccesses.length === 0) {
          this.currentAllowedCertificationCenterAccess = null;
          this.currentCertificationCenterMembership = null;
          this.isAdminOfCurrentCertificationCenter = false;
          return;
        }

        const urlParams = new URLSearchParams(window.location.search);
        const urlCenterId = urlParams.get('centerId');
        const storedCenterId = localStorage.getItem('currentCenterId');
        const preferredCenterId = urlCenterId || storedCenterId;

        if (preferredCenterId) {
          const preferredCenter = allowedCertificationCenterAccesses.find((center) => center.id === preferredCenterId);

          if (preferredCenter) {
            this.currentAllowedCertificationCenterAccess = preferredCenter;
          } else {
            this.currentAllowedCertificationCenterAccess = allowedCertificationCenterAccesses[0];
          }
        } else {
          this.currentAllowedCertificationCenterAccess = allowedCertificationCenterAccesses[0];
        }

        if (this.currentAllowedCertificationCenterAccess) {
          localStorage.setItem('currentCenterId', this.currentAllowedCertificationCenterAccess.id);
        }

        this.currentCertificationCenterMembership = this._findCertificationCenterMembershipByCertificationCenterId(
          this.currentAllowedCertificationCenterAccess?.id,
        );
        await this._updateCurrentUserAccessToCertificationCenter();
        this.isAdminOfCurrentCertificationCenter = this.currentCertificationCenterMembership?.isAdmin;
      } catch {
        this.certificationPointOfContact = null;
        this.currentAllowedCertificationCenterAccess = null;
        this.currentCertificationCenterMembership = null;
        this.isAdminOfCurrentCertificationCenter = false;

        return this.session.invalidate();
      }
    }
  }

  checkRestrictedAccess() {
    if (
      this.certificationPointOfContact.isMemberOfACertificationCenter &&
      this.currentAllowedCertificationCenterAccess.isAccessRestricted
    ) {
      return this.router.replaceWith('authenticated.restricted-access');
    }
  }

  async updateCurrentCertificationCenter(certificationCenterId) {
    if (this.currentAllowedCertificationCenterAccess.id !== String(certificationCenterId)) {
      this.currentAllowedCertificationCenterAccess =
        this.certificationPointOfContact.allowedCertificationCenterAccesses.find(
          ({ id }) => id === String(certificationCenterId),
        );

      localStorage.setItem('currentCenterId', certificationCenterId);

      this.currentCertificationCenterMembership =
        this._findCertificationCenterMembershipByCertificationCenterId(certificationCenterId);
      this.isAdminOfCurrentCertificationCenter = this.currentCertificationCenterMembership?.isAdmin;
      await this._updateCurrentUserAccessToCertificationCenter();
    }
  }

  _findCertificationCenterMembershipByCertificationCenterId(certificationCenterId) {
    return this.certificationPointOfContact.certificationCenterMemberships.find(
      ({ certificationCenterId: id }) => id === Number(certificationCenterId),
    );
  }

  async _updateCurrentUserAccessToCertificationCenter() {
    if (this.currentCertificationCenterMembership) {
      await this.currentCertificationCenterMembership.save({
        adapterOptions: {
          updateLastAccessedAt: true,
        },
      });
    }
  }
}
